import { useMutation, useQueryClient } from '@tanstack/react-query';

import AccessGuard from 'components/shared/AccessGuard';
import AIButton from 'components/shared/ai/AIButton';
import SummaryContentView from 'components/shared/ai/summary/SummaryContentView';
import { useTrackAIJob } from 'hooks/useTrackAIJob';
import { AIJobType } from 'models/AIJob';
import { PERMISSIONS } from 'models/Profile';
import Admission from 'models/reviews/Admission';
import { admissionQueryKeys, admissionQueryParams } from 'services/api/admissions';
import { createAIJob } from 'services/api/aiJobs';
import { AIJobNotificationType } from 'stores/aiJobNotificationStore';
import CriteriaFailedIcon from 'svg/CriteriaFailedIcon';

import AdmissionEmptyState from './AdmissionEmptyState';
import AdmissionRetryLoadingState from './AdmissionRetryLoadingState';

type Props = {
  admission: Admission;
};

export default function AdmissionSummaryTab({ admission }: Props) {
  const queryClient = useQueryClient();
  const { startTracking } = useTrackAIJob();
  const { summaryJob, summary } = admission;
  const queryKey = admissionQueryKeys.show({ id: admission.id });

  const { mutate: retrySummary, isPending: isRetrying } = useMutation({
    mutationFn: () =>
      createAIJob({
        type: 'summary',
        reviewId: admission.id,
        include: admissionQueryParams.show(admission.id).include,
      }),
    onSuccess: (newJob) => {
      // Subscribe to the new job id immediately so we don't miss the websocket
      // event in the gap before the show-query refetch lands.
      startTracking({
        jobs: [{ id: newJob.id, type: AIJobType.SUMMARY }],
        jobType: AIJobNotificationType.ADMISSION_SUMMARY,
        jobbableId: admission.id,
        returnUrl: `/admissions/${admission.id}`,
      });
      queryClient.invalidateQueries({ queryKey });
    },
  });

  if (summaryJob?.isErrored) {
    return (
      <AdmissionEmptyState
        icon={<CriteriaFailedIcon />}
        text={
          <>
            <strong>Summary failed to process.</strong> Click below to retry.
          </>
        }
        action={
          <AccessGuard accessor={PERMISSIONS.reviewAdmissionCreate}>
            <AIButton onClick={() => retrySummary()} loading={isRetrying}>
              Retry
            </AIButton>
          </AccessGuard>
        }
      />
    );
  }

  if (summaryJob?.isProcessing) {
    return (
      <AdmissionRetryLoadingState
        retryingTitle='Retrying summary'
        fileCount={admission.attachments.length}
        isProcessing
      />
    );
  }

  if (!summary) return null;

  return (
    <SummaryContentView
      summaryJob={summaryJob}
      summary={summary}
      attachments={admission.attachments}
      attachmentType='Review::Admission'
      queryKey={queryKey}
    />
  );
}
